import { useRef, useState } from 'react';
import { ImageUp } from 'lucide-react';
import { Button, cn, toast } from '@ludolio/shared-ui';
import { SectionTitle } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import type { LabProjectDetail, LabRelease } from '@/lib/types';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function ImageSlot({
  label,
  src,
  kind,
  release,
  editable,
  onChange,
}: {
  label: string;
  src: string | null;
  kind: 'icon' | 'banner';
  release: LabRelease;
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Images must be smaller than 10 MB');
      return;
    }
    setUploading(true);
    try {
      onChange(await labsApi.uploadMedia(release.id, kind, file));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">{label}</p>
      <div className={cn('overflow-hidden rounded-lg border bg-muted', kind === 'icon' ? 'aspect-square w-32' : 'aspect-video w-full')}>
        {src ? <img src={src} alt={`${label} preview`} className="h-full w-full object-cover" /> : null}
      </div>
      {editable && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void upload(file);
            }}
          />
          <Button type="button" variant="outline" size="sm" className="w-fit" disabled={uploading} onClick={() => inputRef.current?.click()}>
            <ImageUp />
            {uploading ? 'Uploading…' : src ? 'Replace' : 'Upload'}
          </Button>
        </>
      )}
    </div>
  );
}

export function ReleaseImages(props: {
  release: LabRelease;
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  return (
    <section>
      <SectionTitle>Images</SectionTitle>
      <div className="grid gap-6 sm:grid-cols-[8rem_minmax(0,1fr)]">
        <ImageSlot label="Icon" kind="icon" src={props.release.iconUrl} {...props} />
        <ImageSlot label="Banner" kind="banner" src={props.release.bannerUrl} {...props} />
      </div>
    </section>
  );
}
